// LUMINA · pages/AdminOracleStats.jsx
// Oracle 问卜统计面板 — Phase 101 P1-3
// 纯 CSS 柱状图 · 零外部 chart 依赖

import React, { useState, useEffect } from "react";
import { C } from "../constants/baziData";

const API = "/api";

const card = {
  background: "rgba(255,255,255,.80)",
  border: "1px solid rgba(212,175,55,.16)",
  borderRadius: 10,
  padding: "14px 16px",
};

const sectionTitle = { fontSize: 14, color: C.gold, fontWeight: 600, marginBottom: 12, letterSpacing: 0.4 };

export default function AdminOracleStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const token = localStorage.getItem("lumina_token");

  useEffect(() => {
    fetch(`${API}/admin/oracle-stats`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json();
      })
      .then(d => { setStats(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  if (loading) return (
    <div style={{ textAlign: "center", padding: 60, color: C.mist4 }}>加载中…</div>
  );
  if (error) return (
    <div style={{ textAlign: "center", padding: 60, color: C.stress }}>
      加载失败（{error}）— 请确认 Admin 权限
    </div>
  );
  if (!stats) return null;

  const { overview, module_distribution, daily_trend, pro_conversion, recent_thumbs_down } = stats;
  const maxQ = Math.max(...daily_trend.map(d => d.queries), 1);

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>

      {/* 总览卡片行 */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 24 }}>
        {[
          { label: "总查询数", value: overview.total_queries, color: C.mist },
          { label: "今日查询", value: overview.today_queries, color: C.cyan },
          { label: "👍 好评率",  value: `${(overview.thumbs_up_rate * 100).toFixed(0)}%`, color: C.sage },
          { label: "👎 差评率",  value: `${(overview.thumbs_down_rate * 100).toFixed(0)}%`, color: C.stress },
          { label: "无反馈",    value: `${(overview.no_feedback_rate * 100).toFixed(0)}%`, color: C.mist4 },
        ].map((s, i) => (
          <div key={i} style={{ ...card, textAlign: "center" }}>
            <div style={{ fontSize: 11, color: C.mist4, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 8 }}>
              {s.label}
            </div>
            <div style={{ fontSize: 26, fontWeight: 700, color: s.color }}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* 每日趋势 — 纯CSS柱状图 */}
      <div style={{ ...card, marginBottom: 24 }}>
        <div style={sectionTitle}>📈 每日趋势（过去30天）</div>
        {daily_trend.length === 0 ? (
          <div style={{ color: C.mist4, fontSize: 13 }}>暂无数据</div>
        ) : (
          <div style={{
            display: "flex", alignItems: "flex-end", gap: 2, height: 120,
            padding: "4px 0 0", overflow: "hidden",
          }}>
            {[...daily_trend].reverse().slice(0, 30).map((d, i) => {
              const h = Math.max(2, Math.round((d.queries / maxQ) * 112));
              return (
                <div
                  key={i}
                  title={`${d.date}: ${d.queries}次 👍${d.thumbs_up} 👎${d.thumbs_down}`}
                  style={{
                    flex: 1, minWidth: 6, height: h,
                    background: `linear-gradient(to top, ${C.goldDim}, ${C.gold})`,
                    borderRadius: "2px 2px 0 0",
                    opacity: 0.6 + (d.queries / maxQ) * 0.4,
                    cursor: "default",
                  }}
                />
              );
            })}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: C.mist4, marginTop: 4 }}>
          <span>{daily_trend.length > 0 ? [...daily_trend].reverse()[0]?.date : ""}</span>
          <span>{daily_trend[0]?.date}</span>
        </div>
      </div>

      {/* 模块分布 */}
      <div style={{ ...card, marginBottom: 24 }}>
        <div style={sectionTitle}>🎯 模块分布</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {module_distribution.length === 0 ? (
            <div style={{ color: C.mist4, fontSize: 13 }}>暂无数据</div>
          ) : module_distribution.map(m => (
            <div key={m.module} style={{
              background: "rgba(255,255,255,.9)", border: "1px solid rgba(212,175,55,.2)",
              borderRadius: 6, padding: "6px 14px", fontSize: 13,
              display: "flex", gap: 10, alignItems: "center",
            }}>
              <span style={{ color: C.mist3, fontWeight: 500 }}>{m.module}</span>
              <span style={{ color: C.gold, fontWeight: 700 }}>{m.count}</span>
              <span style={{ color: C.mist4, fontSize: 11 }}>{(m.pct * 100).toFixed(0)}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* PRO转化 */}
      <div style={{ ...card, marginBottom: 24 }}>
        <div style={sectionTitle}>💰 PRO转化（近7天）</div>
        <div style={{ display: "flex", gap: 32, fontSize: 14, color: C.mist3 }}>
          <div>
            Oracle用户
            <div style={{ fontSize: 24, fontWeight: 700, color: C.mist, marginTop: 2 }}>
              {pro_conversion.oracle_users_7d}
            </div>
          </div>
          <div>
            购买PRO
            <div style={{ fontSize: 24, fontWeight: 700, color: C.sage, marginTop: 2 }}>
              {pro_conversion.pro_users_7d}
            </div>
          </div>
          <div>
            转化率
            <div style={{ fontSize: 24, fontWeight: 700, color: C.gold, marginTop: 2 }}>
              {(pro_conversion.rate * 100).toFixed(1)}%
            </div>
          </div>
        </div>
        <div style={{ fontSize: 11, color: C.mist4, marginTop: 8 }}>
          ※ 统计已登录的 Oracle 用户（有 user_id 的查询）中近7天内购买 PRO 报告的比例
        </div>
      </div>

      {/* 差评案例 */}
      <div style={card}>
        <div style={sectionTitle}>🔍 最近差评（最多10条）</div>
        {!recent_thumbs_down?.length ? (
          <div style={{ color: C.mist4, fontSize: 13 }}>暂无差评 🎉</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {recent_thumbs_down.map(r => (
              <div key={r.id} style={{
                padding: "8px 14px",
                background: "rgba(255,255,255,.9)",
                border: "1px solid rgba(220,38,38,.12)",
                borderRadius: 6,
                display: "flex", gap: 14, alignItems: "center", fontSize: 13,
              }}>
                <span style={{
                  color: "#fff", background: C.stress, borderRadius: 4,
                  padding: "2px 8px", fontSize: 11, whiteSpace: "nowrap",
                }}>
                  {r.routed_module}
                </span>
                <span style={{ color: C.mist3, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.query_text || "（无文本）"}
                </span>
                <span style={{ color: C.mist4, fontSize: 11, whiteSpace: "nowrap" }}>
                  {r.created_at?.slice(0, 10)}
                </span>
                <span style={{ color: C.mist4, fontSize: 11, whiteSpace: "nowrap" }}>
                  ID {r.id}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
